/* RESPONSIBLE TEAM: team-inbox */
import { dependentKeyCompat } from '@ember/object/compat';
import { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import Predicate from 'predicates/models/predicate';
import type CompanyAssociationEditor from 'predicates/components/editors/company-association-editor';
import type Company from 'embercom/objects/inbox/company';
import type CompanyApi from 'embercom/services/company-api';

export interface APICompany {
  id: string;
  name: string;
  remoteCompanyId: string;
}

class NoComputedPredicate extends Predicate {
  constructor() {
    super(...arguments);

    // @ts-expect-error description is a computed property on Predicate
    delete this.description;
  }
}

export default class CompanyAssociationPredicate extends NoComputedPredicate {
  @service declare intl: $TSFixMe;
  @service declare companyApi: CompanyApi;
  isEditable = true;
  genericType = 'companyAssociation';
  defaultComparison = 'eq';

  // Override typeEditor to use our custom editor instead of select-editor
  typeEditor = 'company-association-editor';

  // This is used to prevent the loader flash.
  static justSelectedCompanies = new Map<CompanyAssociationEditor, Company>();

  @tracked private companyBackingValue: Company | null = null;

  get company() {
    let justSelectedCompanies = new Map(
      [...CompanyAssociationPredicate.justSelectedCompanies.values()].map((company) => [
        company.id,
        company,
      ]),
    );

    let company: Company | null = null;
    if (this.companyBackingValue?.id === this.value) {
      company = this.companyBackingValue;
    } else if (justSelectedCompanies.has(this.value)) {
      company = justSelectedCompanies.get(this.value) ?? null;
      queueMicrotask(() => {
        this.companyBackingValue = company;
      });
    }

    if (this.value && !company) {
      let savedValue = this.value;
      (async () => {
        let company = await this.companyApi.fetchCompany(this.value);
        if (this.value === savedValue && this.value === company?.id) {
          this.companyBackingValue = company;
        }
      })();
    }

    return company ?? null;
  }

  // Make sure `company` property accesses are tracked by way of this decorator.
  // The body of the getter should be the same as in predicate.js.
  @dependentKeyCompat
  get description() {
    return this.getDescription().shortText;
  }
}
